import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Upload, Trash2, FileText, Wallet, CreditCard } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import { todayISO, formatCurrency } from '../utils/format';

const addMonths = (d, n) => {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1);
  const last = new Date(r.getFullYear(), r.getMonth() + 1, 0).getDate();
  r.setDate(Math.min(d.getDate(), last));
  return r;
};

// Datas previstas de cada parcela (mesma regra do backend)
const parcelaDate = (iso, i, intervalo) => {
  const [y, m, d] = iso.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  if (intervalo === 'mensal') return addMonths(base, i);
  return new Date(y, m - 1, d + (intervalo === 'quinzenal' ? 15 : 7) * i);
};
const fmtBR = (dt) => dt.toLocaleDateString('pt-BR');

const ACCENT = { RECEITA: '#10B981', DESPESA: '#EF4444', CUSTO: '#F59E0B' };
const STATUS_OPTS = ['pago', 'pendente', 'atrasado'];
const FORMA_OPTS = ['PIX', 'Boleto', 'Cartão', 'TED-DOC', 'Dinheiro'];

const initialForm = {
  tipo: 'RECEITA', categoriaId: '', descricao: '', valor: '',
  dataCompetencia: todayISO(), dataPagamento: '', status: 'pago',
  formaPagamento: 'PIX', observacoes: '', carteiraId: '', cartaoId: '',
};

export default function LancamentoModal({ isOpen, onClose, lancamento = null, onSuccess }) {
  const [form, setForm] = useState(initialForm);
  const [categorias, setCategorias] = useState([]);
  const [carteiras, setCarteiras] = useState([]);
  const [cartoes, setCartoes] = useState([]);
  const [destinoTipo, setDestinoTipo] = useState('carteira'); // 'carteira' | 'cartao'
  const [parcelar, setParcelar] = useState(false);
  const [parcelas, setParcelas] = useState(2);
  const [intervalo, setIntervalo] = useState('mensal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();

  // Load carteiras e cartões on open
  useEffect(() => {
    if (!isOpen) return;
    api.get('/carteiras').then(r => setCarteiras(r.data || [])).catch(() => {});
    api.get('/cartoes').then(r => setCartoes(r.data || [])).catch(() => {});
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    if (lancamento) {
      const isCartao = Boolean(lancamento.cartaoId);
      setDestinoTipo(isCartao ? 'cartao' : 'carteira');
      setForm({
        tipo: lancamento.tipo || 'RECEITA',
        categoriaId: lancamento.categoriaId || '',
        descricao: lancamento.descricao || '',
        valor: lancamento.valor || '',
        dataCompetencia: lancamento.dataCompetencia || todayISO(),
        dataPagamento: lancamento.dataPagamento || '',
        status: lancamento.status || 'pago',
        formaPagamento: lancamento.formaPagamento || (isCartao ? 'Cartão' : 'PIX'),
        observacoes: lancamento.observacoes || '',
        carteiraId: lancamento.carteiraId || '',
        cartaoId: lancamento.cartaoId || '',
      });
    } else {
      setDestinoTipo('carteira');
      setForm(initialForm);
    }
    setFiles([]);
    setParcelar(false); setParcelas(2); setIntervalo('mensal');
    setError('');
    setDuplicateInfo(null);
  }, [isOpen, lancamento]);

  // Fetch categories when tipo changes
  useEffect(() => {
    if (!isOpen) return;
    api.get(`/categorias?tipo=${form.tipo}`).then(res => {
      setCategorias(res.data);
      if (!lancamento && res.data.length > 0) {
        setForm(prev => ({ ...prev, categoriaId: prev.categoriaId || res.data[0].id }));
      }
    }).catch(() => {});
  }, [form.tipo, isOpen, lancamento]);

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleTipoChange = (tipo) => setForm(prev => ({ ...prev, tipo, categoriaId: '' }));

  const handleSubmit = async (confirmar = false) => {
    if (!form.descricao || !form.valor || !form.dataCompetencia || !form.categoriaId) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload = {
        ...form,
        valor: parseFloat(form.valor),
        carteiraId: destinoTipo === 'carteira' ? (form.carteiraId || null) : null,
        cartaoId: destinoTipo === 'cartao' ? (form.cartaoId || null) : null,
        formaPagamento: destinoTipo === 'cartao' ? 'Cartão' : form.formaPagamento,
      };

      let lancId;
      if (lancamento?.id) {
        await api.put(`/lancamentos/${lancamento.id}`, payload);
        lancId = lancamento.id;
      } else if (parcelar) {
        const nParc = parseInt(parcelas, 10);
        if (!nParc || nParc < 2 || nParc > 120) {
          setError('Informe entre 2 e 120 parcelas.');
          setLoading(false);
          return;
        }
        const res = await api.post('/lancamentos/parcelado', { ...payload, parcelas: nParc, intervalo });
        lancId = res.data.lancamentos[0].id;
      } else {
        const res = await api.post(`/lancamentos?confirmar_duplicata=${confirmar}`, payload);
        lancId = res.data.id;
      }
      // Upload files
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        await api.post(`/lancamentos/${lancId}/anexos`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.response?.status === 409) {
        setDuplicateInfo(err.response.data.detail);
        setLoading(false);
        return;
      }
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Erro ao salvar lançamento');
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...dropped]);
  };

  const removeFile = (i) => setFiles(prev => prev.filter((_, idx) => idx !== i));

  if (!isOpen) return null;


  return createPortal((
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-y-auto"
      style={{ background: 'rgba(15,23,42,0.5)', backdropFilter: 'blur(4px)' }}
      data-testid="lancamento-modal-overlay"
      onClick={onClose}
    >
      <div
        className="nexus-modal w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '90vh' }}
        data-testid="lancamento-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header — fixed */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <h2 className="text-base font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {lancamento?.id ? 'Editar Lançamento' : 'Novo Lançamento'}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="modal-close-btn">
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1 min-h-0">

        {/* Duplicate confirmation */}
        {duplicateInfo && (
          <div className="mx-5 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm font-semibold text-amber-800 mb-1">Lançamento duplicado detectado</p>
            <p className="text-xs text-amber-700 mb-2">
              Já existe um lançamento com a mesma descrição, valor e data. Deseja salvar mesmo assim?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setDuplicateInfo(null)}
                className="fin-btn-secondary text-xs px-3 py-1.5"
                data-testid="modal-cancel-duplicate-btn"
              >
                Cancelar
              </button>
              <button
                onClick={() => { setDuplicateInfo(null); handleSubmit(true); }}
                className="bg-amber-500 hover:bg-amber-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                data-testid="modal-confirm-duplicate-btn"
              >
                Salvar mesmo assim
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mx-5 mt-3 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600" data-testid="modal-error">
            {error}
          </div>
        )}

        {/* Body */}
        <div className="p-5 space-y-3">
          {/* Tipo */}
          <div>
            <label className="fin-label">Tipo *</label>
            <div className="flex gap-2" data-testid="modal-tipo-selector">
              {['RECEITA', 'DESPESA', 'CUSTO'].map(tipo => (
                <button
                  key={tipo}
                  onClick={() => handleTipoChange(tipo)}
                  data-testid={`modal-tipo-${tipo.toLowerCase()}`}
                  className={`flex-1 py-2 rounded-2xl text-xs font-bold uppercase tracking-wider border transition-all ${
                    form.tipo === tipo
                      ? 'border-transparent text-white shadow-md'
                      : 'border-transparent bg-slate-100/70 text-slate-500 hover:bg-white hover:text-slate-800'
                  }`}
                  style={form.tipo === tipo ? { background: ACCENT[tipo] } : {}}
                >
                  {tipo}
                </button>
              ))}
            </div>
          </div>

          {/* Row: Categoria + Status */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="fin-label">Categoria *</label>
              <select
                value={form.categoriaId}
                onChange={e => set('categoriaId', e.target.value)}
                className="fin-input"
                data-testid="modal-categoria-select"
              >
                <option value="">Selecione...</option>
                {categorias.map(c => (
                  <option key={c.id} value={c.id}>{c.nome}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="fin-label">Status *</label>
              <select
                value={form.status}
                onChange={e => set('status', e.target.value)}
                className="fin-input"
                data-testid="modal-status-select"
              >
                {STATUS_OPTS.map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Destino Financeiro: Carteira OU Cartão de Crédito */}
          <div>
            {form.tipo === 'RECEITA' ? (
              <>
                <label className="fin-label flex items-center gap-1">
                  <Wallet size={12} className="text-slate-400" /> Carteira / Conta de Entrada
                </label>
                {carteiras.length === 0 ? (
                  <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                    <Wallet size={14} className="text-amber-500 flex-shrink-0" />
                    <span>Nenhuma carteira cadastrada.{' '}
                      <button
                        type="button"
                        onClick={() => { onClose(); navigate(`/${tenantSlug}/carteira`); }}
                        className="font-semibold underline hover:no-underline"
                      >
                        Cadastre uma aqui
                      </button>
                    </span>
                  </div>
                ) : (
                  <select
                    value={form.carteiraId}
                    onChange={e => set('carteiraId', e.target.value)}
                    className="fin-input"
                    data-testid="modal-carteira-select"
                  >
                    <option value="">Sem carteira definida</option>
                    {carteiras.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}{c.conta ? ` — ${c.conta}` : ''}</option>
                    ))}
                  </select>
                )}
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="fin-label mb-0">Origem do Pagamento *</label>
                  <div className="flex items-center p-0.5 bg-slate-100 dark:bg-slate-800 rounded-lg text-[11px] font-semibold">
                    <button
                      type="button"
                      onClick={() => {
                        setDestinoTipo('carteira');
                        set('cartaoId', '');
                      }}
                      className={`px-2.5 py-1 rounded-md transition-all ${
                        destinoTipo === 'carteira'
                          ? 'bg-white dark:bg-slate-700 text-slate-800 dark:text-white shadow-sm'
                          : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                      }`}
                    >
                      Conta / Carteira
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setDestinoTipo('cartao');
                        set('carteiraId', '');
                        set('formaPagamento', 'Cartão');
                        if (!form.cartaoId && cartoes.length > 0) {
                          set('cartaoId', cartoes[0].id);
                        }
                      }}
                      className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 ${
                        destinoTipo === 'cartao'
                          ? 'bg-indigo-600 text-white shadow-sm font-bold'
                          : 'text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400'
                      }`}
                    >
                      <CreditCard size={11} />
                      <span>Cartão de Crédito</span>
                    </button>
                  </div>
                </div>

                {destinoTipo === 'carteira' ? (
                  carteiras.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                      <Wallet size={14} className="text-amber-500 flex-shrink-0" />
                      <span>Nenhuma carteira cadastrada.{' '}
                        <button
                          type="button"
                          onClick={() => { onClose(); navigate(`/${tenantSlug}/carteira`); }}
                          className="font-semibold underline hover:no-underline"
                        >
                          Cadastre uma aqui
                        </button>
                      </span>
                    </div>
                  ) : (
                    <select
                      value={form.carteiraId}
                      onChange={e => set('carteiraId', e.target.value)}
                      className="fin-input"
                      data-testid="modal-carteira-select"
                    >
                      <option value="">Sem carteira definida</option>
                      {carteiras.map(c => (
                        <option key={c.id} value={c.id}>{c.nome}{c.conta ? ` — ${c.conta}` : ''}</option>
                      ))}
                    </select>
                  )
                ) : (
                  cartoes.length === 0 ? (
                    <div className="flex items-center gap-2 p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-700">
                      <CreditCard size={14} className="text-indigo-500 flex-shrink-0" />
                      <span>Nenhum cartão de crédito cadastrado.{' '}
                        <button
                          type="button"
                          onClick={() => { onClose(); navigate(`/${tenantSlug}/cartoes`); }}
                          className="font-semibold underline hover:no-underline"
                        >
                          Cadastre um cartão aqui
                        </button>
                      </span>
                    </div>
                  ) : (
                    <select
                      value={form.cartaoId}
                      onChange={e => {
                        set('cartaoId', e.target.value);
                        set('carteiraId', '');
                        set('formaPagamento', 'Cartão');
                      }}
                      className="fin-input"
                      data-testid="modal-cartao-select"
                    >
                      <option value="">Selecione o cartão de crédito...</option>
                      {cartoes.map(c => (
                        <option key={c.id} value={c.id}>
                          {c.nome} (Disp: {formatCurrency(c.limiteDisponivel)} • Fecha dia {c.diaFechamento})
                        </option>
                      ))}
                    </select>
                  )
                )}
              </div>
            )}
          </div>

          {/* Descrição */}
          <div>
            <label className="fin-label">Descrição *</label>
            <input
              type="text"
              value={form.descricao}
              onChange={e => set('descricao', e.target.value)}
              className="fin-input"
              placeholder="Ex: Consultoria Mensal"
              data-testid="modal-descricao-input"
            />
          </div>

          {/* Row: Valor + Forma */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="fin-label">Valor (R$) *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.valor}
                onChange={e => set('valor', e.target.value)}
                className="fin-input"
                placeholder="0,00"
                data-testid="modal-valor-input"
              />
            </div>
            <div>
              <label className="fin-label">Forma de Pagamento *</label>
              <select
                value={form.formaPagamento}
                onChange={e => set('formaPagamento', e.target.value)}
                className="fin-input"
                data-testid="modal-forma-select"
              >
                {FORMA_OPTS.map(f => (
                  <option key={f} value={f}>{f}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row: Data Competência + Data Pagamento */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="fin-label">Data de Competência *</label>
              <input
                type="date"
                value={form.dataCompetencia}
                onChange={e => set('dataCompetencia', e.target.value)}
                className="fin-input"
                data-testid="modal-data-competencia-input"
              />
            </div>
            <div>
              <label className="fin-label">Data de Pagamento</label>
              <input
                type="date"
                value={form.dataPagamento}
                onChange={e => set('dataPagamento', e.target.value)}
                className="fin-input"
                data-testid="modal-data-pagamento-input"
              />
            </div>
          </div>

          {/* Parcelamento (só em lançamentos novos) */}
          {!lancamento?.id && (
            <div className="rounded-2xl border border-slate-200/80 dark:border-white/10 p-3.5 space-y-3" data-testid="parcelamento-box">
              <label className="flex items-center justify-between gap-3 cursor-pointer select-none">
                <span>
                  <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Parcelar este lançamento</span>
                  <span className="block text-xs text-slate-400">
                    {form.tipo === 'RECEITA' ? 'Ex.: venda de R$ 4.000 recebida em 10x' : 'Divide o valor em várias parcelas mensais'}
                  </span>
                </span>
                <input
                  type="checkbox"
                  checked={parcelar}
                  onChange={e => setParcelar(e.target.checked)}
                  className="w-5 h-5 accent-indigo-500"
                  data-testid="parcelar-toggle"
                />
              </label>

              {parcelar && (() => {
                const n = parseInt(parcelas, 10);
                const total = parseFloat(form.valor);
                const ok = n >= 2 && n <= 120 && total > 0 && form.dataCompetencia;
                const porParcela = ok ? Math.round(total * 100 / n) / 100 : 0;
                return (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="fin-label">Nº de parcelas</label>
                        <input
                          type="number" min="2" max="120" value={parcelas}
                          onChange={e => setParcelas(e.target.value)}
                          className="fin-input" data-testid="parcelas-input"
                        />
                      </div>
                      <div>
                        <label className="fin-label">Intervalo</label>
                        <select value={intervalo} onChange={e => setIntervalo(e.target.value)} className="fin-input" data-testid="intervalo-select">
                          <option value="mensal">Mensal</option>
                          <option value="quinzenal">A cada 15 dias</option>
                          <option value="semanal">Semanal</option>
                        </select>
                      </div>
                    </div>
                    {ok ? (
                      <div className="text-xs text-slate-500 dark:text-slate-400 space-y-1" data-testid="parcelamento-preview">
                        <p>
                          <strong className="text-slate-800 dark:text-slate-100">{n}x de {formatCurrency(porParcela)}</strong>
                          {' '}— o valor informado acima é o <strong>total</strong> ({formatCurrency(total)}).
                        </p>
                        <p>
                          1ª parcela em {fmtBR(parcelaDate(form.dataCompetencia, 0, intervalo))} · última em {fmtBR(parcelaDate(form.dataCompetencia, n - 1, intervalo))}.
                        </p>
                        <p>
                          A 1ª parcela usa o status escolhido; as demais ficam como <strong>{form.tipo === 'RECEITA' ? 'a receber' : 'a pagar'}</strong> (pendentes), vencendo na data de cada parcela.
                        </p>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400">Preencha valor total, data e nº de parcelas (2 a 120) para ver o resumo.</p>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Observações */}
          <div>
            <label className="fin-label">Observações</label>
            <textarea
              value={form.observacoes}
              onChange={e => set('observacoes', e.target.value)}
              className="fin-input resize-none"
              rows={2}
              placeholder="Observações adicionais..."
              data-testid="modal-observacoes-input"
            />
          </div>

          {/* File Upload */}
          <div>
            <label className="fin-label">Anexos</label>
            <div
              className={`border-2 border-dashed rounded-2xl p-3 text-center transition-colors cursor-pointer ${
                isDragging ? 'border-[#6366F1] bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
              }`}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="modal-file-drop-zone"
            >
              <Upload className="mx-auto mb-1 text-slate-400" size={20} />
              <p className="text-xs text-slate-500">Arraste arquivos ou <span className="text-[#6366F1] font-semibold">clique para selecionar</span></p>
              <p className="text-xs text-slate-400 mt-0.5">PDF, DOC, DOCX, JPG, PNG, XLS, XLSX</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx"
                onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)])}
              />
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between px-3 py-1.5 bg-slate-50 rounded-lg text-xs">
                    <div className="flex items-center gap-2 text-slate-600">
                      <FileText size={12} />
                      <span className="truncate max-w-[250px]">{f.name}</span>
                    </div>
                    <button onClick={() => removeFile(i)} className="text-slate-400 hover:text-red-500">
                      <Trash2 size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        </div>{/* end scrollable body */}

        {/* Footer — fixed */}
        <div className="flex items-center justify-end gap-3 px-5 py-3 border-t border-slate-100 flex-shrink-0 bg-white rounded-b-[28px]">
          <button
            onClick={onClose}
            className="fin-btn-secondary"
            data-testid="modal-cancelar-btn"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            onClick={() => handleSubmit(false)}
            disabled={loading}
            className="fin-btn-primary"
            data-testid="modal-salvar-btn"
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : null}
            {lancamento?.id ? 'Atualizar' : 'Salvar Lançamento'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
